import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Search } from "lucide-react";
import { listBookings, clientsExportUrl } from "../lib/api";
import { ARTISTS } from "../lib/artists";
import { STATUS_OPTIONS, STATUS_LABELS } from "../lib/status";
import { ClientRow } from "../components/admin/ClientRow";
import { AddClientRow } from "../components/admin/AddClientRow";

const BOARD_ARTISTS = ARTISTS.filter((a) => a.role !== "Management");
const UNASSIGNED = { id: "__unassigned", name: "Unassigned" };

const artistOf = (b) => {
  const key = (b.preferred_artist || "").trim().toLowerCase();
  const found = BOARD_ARTISTS.find((a) => a.id === key || a.name.toLowerCase() === key);
  return found ? found.id : UNASSIGNED.id;
};

export default function AdminClients() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [artist, setArtist] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      setBookings(await listBookings());
    } catch {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onChanged = (u) => setBookings((cur) => cur.map((b) => (b.id === u.id ? { ...b, ...u } : b)));
  const onDeleted = (id) => setBookings((cur) => cur.filter((b) => b.id !== id));
  const onAdded = (c) => setBookings((cur) => [c, ...cur]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return bookings.filter((b) => {
      if (status !== "all" && !(b.status === status || (status === "archived_done" && b.status === "archived"))) return false;
      if (!term) return true;
      return [b.name, b.email, b.instagram, b.phone, b.notes, b.source].some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [bookings, status, q]);

  const groups = useMemo(() => {
    const map = new Map([...BOARD_ARTISTS, UNASSIGNED].map((a) => [a.id, []]));
    filtered.forEach((b) => map.get(artistOf(b)).push(b));
    return map;
  }, [filtered]);

  const countFor = (id) => bookings.filter((b) => artistOf(b) === id).length;
  const sections = artist === "all" ? [...BOARD_ARTISTS, UNASSIGNED] : [[...BOARD_ARTISTS, UNASSIGNED].find((a) => a.id === artist)];

  return (
    <div data-testid="admin-clients" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-1">Client Board</div>
          <div className="text-3xl font-display" style={{ letterSpacing: "0.06em", fontWeight: 200 }}>
            {bookings.length}
          </div>
          <p className="text-xs text-[#999] mt-1">Click any name, contact, status or note to edit it in place. Pick an artist tab to see (or add) clients for that artist.</p>
        </div>
        <div className="flex gap-3">
          <a href={clientsExportUrl()} data-testid="clients-export" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase border border-[#0A0A0A] px-4 py-2 hover:bg-[#0A0A0A] hover:text-white transition-colors">
            <Download size={14} /> Export CSV
          </a>
          <button onClick={refresh} data-testid="clients-refresh" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase border border-[#0A0A0A] px-4 py-2 hover:bg-[#0A0A0A] hover:text-white transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Artist tabs */}
      <div className="flex flex-wrap gap-2" data-testid="clients-artist-tabs">
        {[{ id: "all", name: "All" }, ...BOARD_ARTISTS, UNASSIGNED].map((a) => {
          const n = a.id === "all" ? bookings.length : countFor(a.id);
          if (a.id === UNASSIGNED.id && n === 0) return null;
          return (
            <button
              key={a.id}
              onClick={() => setArtist(a.id)}
              data-testid={`clients-tab-${a.id}`}
              className={`text-[10px] tracking-[0.25em] uppercase px-3 py-2 border transition-colors ${artist === a.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"}`}
            >
              {a.name} <span className={artist === a.id ? "text-white/60" : "text-[#999]"}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, instagram, note…" data-testid="clients-search" className="w-full bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none pl-9 pr-3 py-2 text-sm" />
        </label>
        <div className="flex flex-wrap gap-1">
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`clients-status-filter-${s}`} className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 border-b-2 transition-colors ${status === s ? "border-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}>
              {s === "all" ? "All statuses" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#666] text-sm tracking-widest uppercase">Loading…</div>
      ) : (
        <div className="space-y-10">
          {sections.map((a) => {
            const rows = groups.get(a.id) || [];
            if (artist === "all" && rows.length === 0) return null;
            return (
              <section key={a.id} data-testid={`clients-section-${a.id}`} className="bg-white border border-[#E5E5E5]">
                <header className="flex items-center justify-between px-4 py-3 border-b border-[#0A0A0A]">
                  <h3 className="font-display text-lg uppercase" style={{ letterSpacing: "0.22em", fontWeight: 300 }}>
                    <span className="text-[#B3261E] mr-2">*</span>{a.name}
                  </h3>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#999]">{rows.length} client{rows.length === 1 ? "" : "s"}</span>
                </header>
                {rows.length === 0 ? (
                  <div className="px-4 py-6 text-xs text-[#999]" data-testid={`clients-empty-${a.id}`}>No clients yet.</div>
                ) : (
                  rows.map((b, i) => (
                    <ClientRow key={b.id} index={i + 1} booking={b} artistId={a.id === UNASSIGNED.id ? b.preferred_artist : a.id} onChanged={onChanged} onDeleted={onDeleted} />
                  ))
                )}
                {a.id !== UNASSIGNED.id && <AddClientRow artistId={a.id} onAdded={onAdded} />}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
